/** Cookie and audience-measurement choice (D-110): only essential cookies until the visitor
 * accepts; the choice is kept in this browser and, once signed in, on the learner's consents.
 * No measurement script is loaded here: `window.agencyosConsent` tells future tools the answer. */

import { useAcademyAccountConsentsCreate } from "@agencyos/api-client";
import { Button } from "@agencyos/ui-web";
import { Cookie } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useSession } from "../../lib/session";

const KEY = "academy.cookies";

declare global {
  interface Window {
    agencyosConsent?: { analytics: boolean };
  }
}

function readChoice(): "accepted" | "refused" | null {
  try {
    const value = localStorage.getItem(KEY);
    return value === "accepted" || value === "refused" ? value : null;
  } catch {
    return null;
  }
}

export function openCookieChoice(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* storage blocked: the banner simply shows again */
  }
  window.dispatchEvent(new Event("academy:cookies"));
}

export function CookieConsent() {
  const { t } = useTranslation();
  const { accessToken } = useSession();
  const save = useAcademyAccountConsentsCreate();
  const [choice, setChoice] = useState(readChoice);

  useEffect(() => {
    const reopen = () => setChoice(readChoice());
    window.addEventListener("academy:cookies", reopen);
    return () => window.removeEventListener("academy:cookies", reopen);
  }, []);

  useEffect(() => {
    window.agencyosConsent = { analytics: choice === "accepted" };
  }, [choice]);

  const decide = (accepted: boolean) => {
    const value = accepted ? "accepted" : "refused";
    try {
      localStorage.setItem(KEY, value);
    } catch {
      /* private window: asked again next time */
    }
    setChoice(value);
    if (accessToken) save.mutate({ data: { analytics: accepted } });
  };

  if (choice) return null;
  return (
    <section className="as-cookies ao-glass" aria-labelledby="as-cookies-title">
      <Cookie aria-hidden size={20} className="as-cookies__icon" />
      <div className="as-cookies__text">
        <h2 id="as-cookies-title" className="as-cookies__title">
          {t("academySales.cookies.title")}
        </h2>
        <p className="ac-footnote">
          {t("academySales.cookies.text")}{" "}
          <Link className="ac-link" to="/academy/legal/cookies">
            {t("academySales.cookies.more")}
          </Link>
        </p>
      </div>
      <div className="as-cookies__actions">
        <Button variant="secondary" size="sm" onClick={() => decide(false)}>
          {t("academySales.cookies.refuse")}
        </Button>
        <Button size="sm" onClick={() => decide(true)}>
          {t("academySales.cookies.accept")}
        </Button>
      </div>
    </section>
  );
}

/** Footer links of the Academy: legal pages and the cookie choice. */
export function LegalFooter() {
  const { t } = useTranslation();
  return (
    <footer className="as-footer">
      <Link to="/academy/legal/terms">{t("academySales.legal.terms")}</Link>
      <Link to="/academy/legal/privacy">{t("academySales.legal.privacy")}</Link>
      <Link to="/academy/legal/refund">{t("academySales.legal.refund")}</Link>
      <button type="button" className="as-footer__button" onClick={openCookieChoice}>
        {t("academySales.cookies.manage")}
      </button>
    </footer>
  );
}
